<template>
  <div class="tab-icon" :style="{ width: `${size}px`, height: `${size}px` }">
    <svg
      :width="size"
      :height="size"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <!-- Home Icon -->
      <template v-if="name === 'home'">
        <path 
          d="M6.66699 17.0134C6.66699 15.2028 6.66699 14.2974 7.03233 13.5014C7.39899 12.7054 8.08566 12.1174 9.46033 10.9388L10.7937 9.7961C13.2803 7.66676 14.5203 6.6001 16.0003 6.6001C17.4803 6.6001 18.7217 7.66543 21.207 9.79476L22.5403 10.9374C23.9137 12.1161 24.6017 12.7041 24.967 13.5001C25.3337 14.2961 25.3337 15.2014 25.3337 17.0121V22.6668C25.3337 25.1814 25.3337 26.4374 24.5523 27.2188C23.771 28.0001 22.515 28.0001 20.0003 28.0001H12.0003C9.48566 28.0001 8.22966 28.0001 7.44833 27.2188C6.66699 26.4374 6.66699 25.1814 6.66699 22.6668V17.0134Z" 
          stroke="currentColor" 
          stroke-width="2.66667"
        />
        <path 
          d="M19.3337 28V21.3333C19.3337 20.9797 19.1932 20.6406 18.9431 20.3905C18.6931 20.1405 18.3539 20 18.0003 20H14.0003C13.6467 20 13.3076 20.1405 13.0575 20.3905C12.8075 20.6406 12.667 20.9797 12.667 21.3333V28" 
          stroke="currentColor" 
          stroke-width="2.66667" 
          stroke-linecap="round" 
          stroke-linejoin="round"
        />
      </template>

      <!-- Compass Icon -->
      <template v-else-if="name === 'compass'">
        <path 
          d="M13.2563 14.2492C13.2219 13.2429 13.2184 12.2358 13.2456 11.2292C14.039 11.6612 14.979 12.1959 15.8563 12.7492C16.4963 13.1519 17.0803 13.5519 17.5363 13.9172C18.0296 14.3106 18.2523 14.5666 18.3096 14.6666C18.367 14.7666 18.479 15.0866 18.5723 15.7106C18.6603 16.2892 18.7136 16.9932 18.7443 17.7506C18.7843 18.7852 18.7763 19.8679 18.755 20.7706C17.869 20.2914 16.9984 19.7845 16.1443 19.2506C15.5043 18.8466 14.9216 18.4466 14.4643 18.0826C13.971 17.6892 13.7483 17.4332 13.691 17.3332C13.6336 17.2332 13.523 16.9132 13.4283 16.2892C13.3362 15.6126 13.2788 14.9317 13.2563 14.2492Z" 
          stroke="currentColor" 
          stroke-width="2.66667"
        />
        <path 
          d="M16 28C22.6274 28 28 22.6274 28 16C28 9.37258 22.6274 4 16 4C9.37258 4 4 9.37258 4 16C4 22.6274 9.37258 28 16 28Z" 
          stroke="currentColor" 
          stroke-width="2.66667"
        />
      </template>

      <!-- User Icon -->
      <template v-else-if="name === 'user'">
        <path 
          d="M16.0003 14.6667C18.9458 14.6667 21.3337 12.2789 21.3337 9.33333C21.3337 6.38781 18.9458 4 16.0003 4C13.0548 4 10.667 6.38781 10.667 9.33333C10.667 12.2789 13.0548 14.6667 16.0003 14.6667Z" 
          stroke="currentColor" 
          stroke-width="2.66667" 
          stroke-linecap="round"
        />
        <path 
          d="M7.11724 24.4267C7.99857 20.7041 11.6959 18.6667 15.5239 18.6667H16.4759C20.3039 18.6667 24.0012 20.7027 24.8826 24.4267C25.0532 25.1481 25.1892 25.9027 25.2652 26.6694C25.3386 27.4027 24.7359 28.0001 23.9999 28.0001H7.9999C7.2639 28.0001 6.66124 27.4014 6.73457 26.6694C6.81057 25.9027 6.94657 25.1481 7.11724 24.4267Z" 
          stroke="currentColor" 
          stroke-width="2.66667" 
          stroke-linecap="round"
        />
      </template>

      <!-- Lock Icon -->
      <template v-else-if="name === 'lock'">
        <path 
          d="M8 14V10C8 6.68629 10.6863 4 14 4H18C21.3137 4 24 6.68629 24 10V14M6 14H26C27.1046 14 28 14.8954 28 16V26C28 27.1046 27.1046 28 26 28H6C4.89543 28 4 27.1046 4 26V16C4 14.8954 4.89543 14 6 14Z" 
          stroke="currentColor" 
          stroke-width="2.66667" 
          stroke-linecap="round" 
          stroke-linejoin="round"
        />
      </template>
    </svg>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';

interface Props {
  name: 'home' | 'compass' | 'user' | 'lock';
  size?: number;
  color?: string;
}

const props = withDefaults(defineProps<Props>(), {
  size: 32,
  color: 'currentColor'
});

const currentColor = computed(() => props.color);
</script>

<style scoped>
.tab-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
}

.tab-icon svg {
  color: inherit;
}

.tab-icon svg path {
  stroke: currentColor;
}
</style> 